/* eslint-disable max-lines-per-function */
import { compact } from 'lodash';
import { ReactElement, useMemo } from 'react';

import { VisitAndAppointmentView } from '@dreryk/egabinet-visit-frontend-api';

import { Dropdown, MoreIconVertical } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { useAppointmentNoteCreateDropdownItem } from '~libs.desktop.shared.appointment';

import { appointmentsTranslationKeys } from '../../../../translations';
import visitSettlement from '../../../../visitSettlement';
import { RefetchAppointments } from '../../../appointments.types';

import { AppointmentCancelDropdownItem } from './AppointmentCancelDropdownItem';
import { AppointmentRestoreDropdownItem } from './AppointmentRestoreDropdownItem';
import { AppointmentTermUpdateDropdownItem } from './AppointmentTermUpdateDropdownItem';
import { PatientFileDropdownItem } from './PatientFileDropdownItem';
import { PatientHistoryDropdownItem } from './PatientHistoryDropdownItem';
import { VisitCancelDropdownItem } from './VisitCancelDropdownItem';
import { useInvoiceCreateDropdownItem } from './useInvoiceCreateDropdownItem';
import { useVisitReopenDropdownItem } from './useVisitReopenDropdownItem';

type AdditionalOptionsCellProps = Pick<
   VisitAndAppointmentView,
   | 'cancellable'
   | 'appointmentId'
   | 'visitId'
   | 'practitioner'
   | 'status'
   | 'patientId'
   | 'ownedByCurrentUser'
   | 'reopened'
   | 'beginDate'
   | 'endDate'
   | 'patientFirstName'
   | 'patientLastName'
   | 'diagnoses'
   | 'encounterMode'
   | 'timeSlotConfirmed'
   | 'note'
> & {
   readonly refetchAppointments: RefetchAppointments;
};

type RenderOptionalDropdownItem = (() => ReactElement) | null;
type RenderDropdownItem = () => ReactElement;

const renderAdditionalOptionsList = (
   status: VisitAndAppointmentView['status'],
   patientId: VisitAndAppointmentView['patientId'],
   renderAppointmentCancelDropdownItem: RenderOptionalDropdownItem,
   renderVisitCancelOption: RenderOptionalDropdownItem,
   renderAppointmentTermUpdateDropdownItem: RenderOptionalDropdownItem,
   renderAppointmentRestoreDropdownItem: RenderDropdownItem,
   renderPatientFileDropdownItem: RenderDropdownItem,
   renderPatientHistoryDropdownItem: RenderOptionalDropdownItem,
   renderVisitReopenDropdownItem: RenderDropdownItem,
   renderVisitSettlementDropdownOption: () => ReactElement | null,
   renderInvoiceCreateDropdownItem: RenderOptionalDropdownItem,
   renderAppointmentNoteCreateDropdownItem: RenderOptionalDropdownItem
   // eslint-disable-next-line max-params
): ReactElement[] => {
   const optionsList: Array<ReactElement | null> = [];
   switch (status) {
      case 'REGISTERED':
         if (renderAppointmentCancelDropdownItem) {
            optionsList.push(renderAppointmentCancelDropdownItem());
         }
         if (renderAppointmentTermUpdateDropdownItem) {
            optionsList.push(renderAppointmentTermUpdateDropdownItem());
         }
         break;
      case 'IN_PROGRESS':
      case 'FINISHED_NOT_APPROVED':
         if (renderVisitCancelOption) {
            optionsList.push(renderVisitCancelOption());
         }
         break;
      case 'CANCELLED':
         optionsList.push(renderAppointmentRestoreDropdownItem());
         break;
      default:
         break;
   }

   if (status === 'FINISHED_APPROVED') {
      optionsList.push(renderVisitReopenDropdownItem());
   }

   if (renderAppointmentNoteCreateDropdownItem) {
      optionsList.push(renderAppointmentNoteCreateDropdownItem());
   }

   optionsList.push(renderVisitSettlementDropdownOption());

   if (renderInvoiceCreateDropdownItem) {
      optionsList.push(renderInvoiceCreateDropdownItem());
   }

   if (patientId) {
      optionsList.push(renderPatientFileDropdownItem());
   }

   if (renderPatientHistoryDropdownItem) {
      optionsList.push(renderPatientHistoryDropdownItem());
   }

   return compact(optionsList);
};

export const AdditionalOptionsCell = (props: AdditionalOptionsCellProps): JSX.Element => {
   const privilegeToCancelOrRestore = protectFeature('searchingAllAppointments', true, props.ownedByCurrentUser);

   const chronicDiseaseExists = useMemo(() => props.diagnoses.some((diagnosis) => diagnosis.chronic), [
      props.diagnoses,
   ]);

   const { VisitReopenDropdownItem, visitReopenModal } = useVisitReopenDropdownItem({ visitId: props.visitId! });
   const { InvoiceCreateDropdownItem, invoiceCreateModal } = useInvoiceCreateDropdownItem({
      patientId: props.patientId,
      appointmentId: props.appointmentId,
      visitDate: props.beginDate,
   });

   const {
      renderVisitSettlementDropdownItem,
      visitSettlementPaymentModal,
      visitSettlementAdjustmentModal,
   } = visitSettlement.useVisitSettlementDropdownOption({
      appointmentId: props.appointmentId,
      practitionerId: props.practitioner.id,
      ownedByCurrentUser: props.ownedByCurrentUser,
      visitDetails: {
         beginDate: props.beginDate,
         endDate: props.endDate,
         patient: {
            firstName: props.patientFirstName?.asString() ?? null,
            lastName: props.patientLastName?.asString() ?? null,
            id: props.patientId,
         },
      },
   });

   const { AppointmentNoteCreateDropdownItem, appointmentNoteCreateModal } = useAppointmentNoteCreateDropdownItem({
      appointmentId: props.appointmentId,
      onAppointmentNoteAdded: props.refetchAppointments,
   });

   return (
      <>
         <Dropdown
            triggerIcon={<MoreIconVertical />}
            triggerIconSuffixHidden
            disabled={'empty'}
            testId={'additionalOptions'}
         >
            {renderAdditionalOptionsList(
               props.status,
               props.patientId,
               protectFeature(
                  'creatingAppointments',
                  () => (
                     <AppointmentCancelDropdownItem
                        key={'appointmentCancel'}
                        appointmentId={props.appointmentId}
                        refetchAppointments={props.refetchAppointments}
                        label={appointmentsTranslationKeys.cancelPlannedAppointment}
                        disabled={!privilegeToCancelOrRestore}
                        timeSlotConfirmed={props.timeSlotConfirmed}
                     />
                  ),
                  null
               ),
               protectFeature(
                  'creatingVisits',
                  () => {
                     const enabled =
                        !props.reopened && props.ownedByCurrentUser && props.cancellable && !chronicDiseaseExists;

                     return (
                        <VisitCancelDropdownItem
                           key={'visitCancel'}
                           visitId={props.visitId}
                           label={appointmentsTranslationKeys.cancel}
                           refetchAppointments={props.refetchAppointments}
                           chronicDiseaseExists={chronicDiseaseExists}
                           enabled={enabled}
                           reopened={props.reopened}
                        />
                     );
                  },
                  null
               ),
               () => (
                  <AppointmentTermUpdateDropdownItem
                     key={'appointmentTermUpdate'}
                     appointmentId={props.appointmentId}
                  />
               ),
               () => (
                  <AppointmentRestoreDropdownItem
                     key={'appointmentRestore'}
                     appointmentId={props.appointmentId}
                     restoreAvailable={props.ownedByCurrentUser}
                  />
               ),
               () => (
                  <PatientFileDropdownItem key={'patientFile'} patientId={props.patientId} />
               ),
               protectFeature(
                  'searchingMedicalData',
                  () => <PatientHistoryDropdownItem key={'patientHistory'} patientId={props.patientId} />,
                  null
               ),
               () => (
                  <VisitReopenDropdownItem
                     key={'visitReopen'}
                     patientId={props.patientId}
                     visitId={props.visitId!}
                     ownedByCurrentUser={props.ownedByCurrentUser}
                     appointmentId={props.appointmentId}
                     encounterMode={props.encounterMode}
                  />
               ),
               renderVisitSettlementDropdownItem,
               protectFeature('creatingInvoices', () => <InvoiceCreateDropdownItem key={'invoiceCreate'} />, null),
               props.note ? null : () => <AppointmentNoteCreateDropdownItem key={'appointmentNoteCreate'} />
            )}
         </Dropdown>
         {visitSettlementPaymentModal}
         {visitSettlementAdjustmentModal}
         {visitReopenModal}
         {invoiceCreateModal}
         {appointmentNoteCreateModal}
      </>
   );
};
